/**
 * Diagrama visual de empalmes de fibra óptica
 * Muestra conexiones entre cables en un punto de empalme
 * @module components/diagrams/SpliceDiagram
 */

import { useState } from 'react'
import { Circle, Download, ZoomIn, ZoomOut, Layers } from 'lucide-react'

const COLORES_FIBRA = [
  { nombre: 'Azul', codigo: 'azul', hex: '#0066CC' },
  { nombre: 'Naranja', codigo: 'naranja', hex: '#FF6600' },
  { nombre: 'Verde', codigo: 'verde', hex: '#00AA00' },
  { nombre: 'Marrón', codigo: 'marron', hex: '#8B4513' },
  { nombre: 'Gris', codigo: 'gris', hex: '#808080' },
  { nombre: 'Blanco', codigo: 'blanco', hex: '#FFFFFF' },
  { nombre: 'Rojo', codigo: 'rojo', hex: '#FF0000' },
  { nombre: 'Negro', codigo: 'negro', hex: '#000000' },
  { nombre: 'Amarillo', codigo: 'amarillo', hex: '#FFCC00' },
  { nombre: 'Violeta', codigo: 'violeta', hex: '#9900CC' },
  { nombre: 'Rosa', codigo: 'rosa', hex: '#FF66CC' },
  { nombre: 'Turquesa', codigo: 'turquesa', hex: '#00CCCC' },
]

interface FiberConnection {
  id: string
  cableOrigenId: string
  cableOrigenNombre: string
  bufferOrigen: number | null
  bufferOrigenColor?: string
  fibraOrigen: number
  colorOrigen: string
  cableDestinoId: string
  cableDestinoNombre: string
  bufferDestino: number | null
  bufferDestinoColor?: string
  fibraDestino: number
  colorDestino: string
  tipoEmpalme: 'fusion' | 'mecanico'
  bandeja: number
  posicion: number
  atenuacion: number
  estado: 'pendiente' | 'realizado' | 'verificado' | 'fallido'
}

interface SpliceDiagramProps {
  puntoEmpalmeNombre: string
  puntoEmpalmeTipo: 'CIERRE' | 'NAP'
  conexiones: FiberConnection[]
  bandejasCount?: number
}

export function SpliceDiagram({
  puntoEmpalmeNombre,
  puntoEmpalmeTipo,
  conexiones,
  bandejasCount = 3,
}: SpliceDiagramProps) {
  void puntoEmpalmeTipo
  const [selectedBandeja, setSelectedBandeja] = useState<number | null>(null)
  const [zoom, setZoom] = useState(1)

  const getColorHex = (colorCodigo: string) => {
    return COLORES_FIBRA.find(c => c.codigo === colorCodigo)?.hex || '#CCCCCC'
  }

  const getEstadoColor = (estado: FiberConnection['estado']) => {
    switch (estado) {
      case 'pendiente': return '#FCD34D'
      case 'realizado': return '#34D399'
      case 'verificado': return '#10B981'
      case 'fallido': return '#EF4444'
      default: return '#9CA3AF'
    }
  }

  const conexionesPorBandeja: Record<number, FiberConnection[]> = {}
  for (let i = 1; i <= bandejasCount; i++) {
    conexionesPorBandeja[i] = conexiones.filter(c => c.bandeja === i)
  }

  const filteredConexiones = selectedBandeja 
    ? conexionesPorBandeja[selectedBandeja] || []
    : conexiones

  const groupedByPair = filteredConexiones.reduce((acc, conn) => {
    const key = `${conn.cableOrigenId}-${conn.cableDestinoId}`
    if (!acc[key]) {
      acc[key] = {
        cableOrigenNombre: conn.cableOrigenNombre,
        cableDestinoNombre: conn.cableDestinoNombre,
        conexiones: []
      }
    }
    acc[key].conexiones.push(conn)
    return acc
  }, {} as Record<string, { cableOrigenNombre: string; cableDestinoNombre: string; conexiones: FiberConnection[] }>)

  const exportSVG = () => {
    const svgElement = document.getElementById('splice-diagram-svg')
    if (!svgElement) return

    const svgData = new XMLSerializer().serializeToString(svgElement)
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' })
    const svgUrl = URL.createObjectURL(svgBlob)
    
    const downloadLink = document.createElement('a')
    downloadLink.href = svgUrl
    downloadLink.download = `empalmes-${puntoEmpalmeNombre}.svg`
    document.body.appendChild(downloadLink)
    downloadLink.click()
    document.body.removeChild(downloadLink)
    URL.revokeObjectURL(svgUrl)
  }

  return (
    <div className="space-y-4">
      {/* Header y controles */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-lg flex items-center gap-2">
            <Layers className="w-5 h-5" />
            Diagrama de Empalmes - {puntoEmpalmeNombre}
          </h3>
          <p className="text-sm text-gray-500">
            {puntoEmpalmeTipo} • {conexiones.length} conexiones • {bandejasCount} bandejas
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Selector de bandeja */}
          <select
            value={selectedBandeja || ''}
            onChange={(e) => setSelectedBandeja(e.target.value ? parseInt(e.target.value) : null)}
            className="px-3 py-1.5 text-sm border rounded-lg dark:bg-dark-700 dark:border-dark-600"
          >
            <option value="">Todas las bandejas</option>
            {Array.from({ length: bandejasCount }, (_, i) => (
              <option key={i + 1} value={i + 1}>
                Bandeja {i + 1} ({conexionesPorBandeja[i + 1]?.length || 0})
              </option>
            ))}
          </select>

          {/* Zoom */}
          <div className="flex items-center gap-1 border rounded-lg dark:border-dark-600">
            <button
              onClick={() => setZoom(z => Math.max(0.5, z - 0.1))}
              className="p-1.5 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-l-lg"
              title="Alejar"
            >
              <ZoomOut className="w-4 h-4" />
            </button>
            <span className="px-2 text-sm">{Math.round(zoom * 100)}%</span>
            <button
              onClick={() => setZoom(z => Math.min(2, z + 0.1))}
              className="p-1.5 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-r-lg"
              title="Acercar"
            >
              <ZoomIn className="w-4 h-4" />
            </button>
          </div>

          {/* Exportar */}
          <button
            onClick={exportSVG}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Download className="w-4 h-4" /> SVG
          </button>
        </div>
      </div>

      {/* Leyenda */}
      <div className="flex items-center gap-6 text-xs bg-gray-50 dark:bg-dark-700 rounded-lg p-3">
        <span className="font-medium">Estado:</span>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: getEstadoColor('pendiente') }}></div>
          <span>Pendiente</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: getEstadoColor('realizado') }}></div>
          <span>Realizado</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: getEstadoColor('verificado') }}></div>
          <span>Verificado</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: getEstadoColor('fallido') }}></div>
          <span>Fallido</span>
        </div>
        <span className="ml-4 font-medium">Tipo:</span>
        <span>🔥 Fusión</span>
        <span>🔧 Mecánico</span>
      </div>

      {/* Diagrama SVG */}
      <div 
        className="border rounded-lg overflow-auto bg-white dark:bg-dark-800 p-4"
        style={{ maxHeight: '600px' }}
      >
        <svg 
          id="splice-diagram-svg"
          viewBox={`0 0 ${800 * zoom} ${Math.max(400, Object.keys(groupedByPair).length * 200) * zoom}`}
          className="w-full"
          style={{ minWidth: `${600 * zoom}px` }}
        >
          {/* Background */}
          <rect width="100%" height="100%" fill="white" />
          
          {/* Título */}
          <text x="400" y="30" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#1F2937">
            {puntoEmpalmeNombre} - Diagrama de Empalmes
          </text>
          <text x="400" y="50" textAnchor="middle" fontSize="12" fill="#6B7280">
            {selectedBandeja ? `Bandeja ${selectedBandeja}` : 'Todas las bandejas'} • {filteredConexiones.length} conexiones
          </text>

          {/* Diagramas por par de cables */}
          {Object.entries(groupedByPair).map(([key, data], pairIndex) => {
            const yOffset = 80 + pairIndex * 180
            
            return (
              <g key={key} transform={`translate(0, ${yOffset})`}>
                {/* Box del diagrama */}
                <rect 
                  x="50" y="0" 
                  width="700" height="160" 
                  fill="#F9FAFB" 
                  stroke="#E5E7EB" 
                  strokeWidth="2"
                  rx="8"
                />
                
                {/* Título del par */}
                <text x="400" y="25" textAnchor="middle" fontSize="12" fontWeight="600" fill="#374151">
                  {data.cableOrigenNombre} ←→ {data.cableDestinoNombre}
                </text>

                {/* Línea central divisoria */}
                <line x1="400" y1="35" x2="400" y2="150" stroke="#D1D5DB" strokeWidth="2" strokeDasharray="4" />

                {/* Headers de cables */}
                <text x="200" y="50" textAnchor="middle" fontSize="11" fontWeight="500" fill="#6B7280">
                  {data.cableOrigenNombre}
                </text>
                <text x="600" y="50" textAnchor="middle" fontSize="11" fontWeight="500" fill="#6B7280">
                  {data.cableDestinoNombre}
                </text>

                {/* Conexiones de fibra */}
                {data.conexiones.slice(0, 6).map((conn, connIndex) => {
                  const y = 70 + connIndex * 15
                  const colorOrigen = getColorHex(conn.colorOrigen)
                  const colorDestino = getColorHex(conn.colorDestino)
                  const estadoColor = getEstadoColor(conn.estado)

                  return (
                    <g key={conn.id}>
                      {/* Fibra origen */}
                      <circle cx="100" cy={y} r="5" fill={colorOrigen} stroke={colorOrigen === '#FFFFFF' ? '#999' : colorOrigen} />
                      <text x="115" y={y + 4} fontSize="9" fill="#4B5563">
                        {conn.bufferOrigen ? `B${conn.bufferOrigen}/` : ''}F{conn.fibraOrigen}
                      </text>

                      {/* Línea de conexión */}
                      <line 
                        x1="170" y1={y} 
                        x2="630" y2={y} 
                        stroke={estadoColor} 
                        strokeWidth="2"
                        strokeDasharray={conn.estado === 'pendiente' ? '4' : 'none'}
                      />

                      {/* Indicador de tipo de empalme en el centro */}
                      <circle cx="400" cy={y} r="8" fill="white" stroke={estadoColor} strokeWidth="2" />
                      <text x="400" y={y + 3} textAnchor="middle" fontSize="8">
                        {conn.tipoEmpalme === 'fusion' ? '🔥' : '🔧'}
                      </text>

                      {/* Atenuación */}
                      <text x="400" y={y + 18} textAnchor="middle" fontSize="8" fill="#F97316">
                        {conn.atenuacion.toFixed(2)} dB
                      </text>

                      {/* Fibra destino */}
                      <text x="665" y={y + 4} fontSize="9" fill="#4B5563" textAnchor="end">
                        {conn.bufferDestino ? `B${conn.bufferDestino}/` : ''}F{conn.fibraDestino}
                      </text>
                      <circle cx="700" cy={y} r="5" fill={colorDestino} stroke={colorDestino === '#FFFFFF' ? '#999' : colorDestino} />

                      {/* Posición */}
                      <text x="60" y={y + 4} fontSize="8" fill="#9CA3AF">
                        #{conn.posicion}
                      </text>
                    </g>
                  )
                })}

                {/* Indicador si hay más conexiones */}
                {data.conexiones.length > 6 && (
                  <text x="400" y="155" textAnchor="middle" fontSize="10" fill="#6B7280">
                    ... y {data.conexiones.length - 6} conexiones más
                  </text>
                )}
              </g>
            )
          })}

          {/* Sin conexiones */}
          {Object.keys(groupedByPair).length === 0 && (
            <text x="400" y="200" textAnchor="middle" fontSize="14" fill="#9CA3AF">
              No hay conexiones para mostrar
            </text>
          )}
        </svg>
      </div>

      {/* Lista detallada (colapsable) */}
      <details className="border rounded-lg dark:border-dark-600">
        <summary className="px-4 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-700 font-medium">
          Ver lista detallada de conexiones ({filteredConexiones.length})
        </summary>
        <div className="p-4 border-t dark:border-dark-600 max-h-64 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-dark-700">
              <tr>
                <th className="px-2 py-1 text-left">Pos.</th>
                <th className="px-2 py-1 text-left">Origen</th>
                <th className="px-2 py-1 text-center">Tipo</th>
                <th className="px-2 py-1 text-left">Destino</th>
                <th className="px-2 py-1 text-center">dB</th>
                <th className="px-2 py-1 text-center">Estado</th>
              </tr>
            </thead>
            <tbody className="divide-y dark:divide-dark-600">
              {filteredConexiones.map(conn => (
                <tr key={conn.id} className="hover:bg-gray-50 dark:hover:bg-dark-800">
                  <td className="px-2 py-1 text-gray-500">B{conn.bandeja}/#{conn.posicion}</td>
                  <td className="px-2 py-1">
                    <div className="flex items-center gap-1">
                      <Circle 
                        className="w-3 h-3 flex-shrink-0" 
                        fill={getColorHex(conn.colorOrigen)} 
                        stroke={getColorHex(conn.colorOrigen)}
                      />
                      <span>{conn.cableOrigenNombre}</span>
                      <span className="text-gray-500">
                        {conn.bufferOrigen && `B${conn.bufferOrigen}/`}F{conn.fibraOrigen}
                      </span>
                    </div>
                  </td>
                  <td className="px-2 py-1 text-center">
                    {conn.tipoEmpalme === 'fusion' ? '🔥' : '🔧'}
                  </td>
                  <td className="px-2 py-1">
                    <div className="flex items-center gap-1">
                      <Circle 
                        className="w-3 h-3 flex-shrink-0" 
                        fill={getColorHex(conn.colorDestino)} 
                        stroke={getColorHex(conn.colorDestino)}
                      />
                      <span>{conn.cableDestinoNombre}</span>
                      <span className="text-gray-500">
                        {conn.bufferDestino && `B${conn.bufferDestino}/`}F{conn.fibraDestino}
                      </span>
                    </div>
                  </td>
                  <td className="px-2 py-1 text-center text-orange-600">{conn.atenuacion.toFixed(2)}</td>
                  <td className="px-2 py-1 text-center">
                    <span 
                      className="inline-block w-3 h-3 rounded-full"
                      style={{ backgroundColor: getEstadoColor(conn.estado) }}
                      title={conn.estado}
                    ></span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>

      {/* Resumen de atenuación */}
      <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4 border border-orange-200 dark:border-orange-800">
        <h4 className="font-medium text-orange-800 dark:text-orange-200 mb-2">Resumen de Atenuación</h4>
        <div className="grid grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-gray-600 dark:text-gray-400">Total empalmes:</span>
            <span className="ml-2 font-bold">{filteredConexiones.length}</span>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-400">Fusión:</span>
            <span className="ml-2 font-bold">{filteredConexiones.filter(c => c.tipoEmpalme === 'fusion').length}</span>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-400">Mecánico:</span>
            <span className="ml-2 font-bold">{filteredConexiones.filter(c => c.tipoEmpalme === 'mecanico').length}</span>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-400">Atenuación total:</span>
            <span className="ml-2 font-bold text-orange-600">
              {filteredConexiones.reduce((acc, c) => acc + c.atenuacion, 0).toFixed(3)} dB
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
